import { Injectable, Inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { FeatureFlagService } from 'projects/auth/src/lib/services/feature-flag.service';

import { ErrorService } from '../error.service';
import { AuthConfig } from 'projects/auth';

/**
 * 'ServerErrorInterceptor' implements 'HttpInterceptor' to intercept http requests and
 * responses globally, so that 'ServerErrorInterceptor' can emit 'error' and
 * redirect user to 'auth_path' soon after recognizing network or server failure.
 * 'ServerErrorInterceptor' is applied to all http requests across the Angular application.
 * If you wish to remove 'ServerErrorInterceptor' from the app,
 * remove 'ServerErrorInterceptor' from providers list in 'AppModule'.
 */
@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private error_service: ErrorService,
    private location: Location,
    private feature_flag_service: FeatureFlagService,
    @Inject('config') private config: AuthConfig,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Log all errors first
        console.error('ServerErrorInterceptor::error: ', error);

        // If the request fails and the error status is 0
        // (indicating a network or server failure),
        // emit error
        if (error.status === 0) {
          this.error_service.emitError(
            new Error(
              `A network or server failure occurred. Consult with IST for the error=${error.message}`,
            ),
          );
        } else if (error.status === 400) {
          // If server responded with 400 (indicating bad request),
          // emit error
          this.error_service.emitError(
            new Error(
              `Bad request. Request failed because of error=${JSON.stringify(error.error)}`,
            ),
          );
        } else if (error.status === 401) {
          // If server responded with 401 (indicating unauthorized),
          // redirect user to login page
          sessionStorage.removeItem('token');

          console.warn('About to redirect user to auth page');

          // emit error, so 'LoginComponent' can subscribe to
          this.error_service.emitError(error);

          if (this.feature_flag_service.is_oidc_auth_enabled) {
            this.router.navigate([...this.config.auth_path, 'oidc-login']);
          } else {
            this.router.navigate([...this.config.auth_path, 'login']);
          }

        } else if (error.status === 403) {
          // Handle RBAC unauthorized error
          // Send a client back to the previous page and alert
          this.error_service.emitError(new Error('Unauthorized. Contact the admin for access'));
          this.location.back();
        } else if (error.status === 404) {
          // status 404 is expected API error to be handled by the client
          console.warn(`Client received ${error.status} for an API call`);
        } else if (error.status === 422) {
          this.error_service.emitError(
            new Error(`Requested file is not acceptable. Please retry with a different file.`),
          );
        } else if (error.status === 423) {
          // If server responded with 423 (indicating service (ex: Mail) locked), emit error
          this.error_service.emitError(
            new Error(
              `Request service is currently locked because of error=${JSON.stringify(error.error)}`,
            ),
          );
        } else {
          // For all the other errors
          this.error_service.emitError(
            new Error(
              `Unexpected error occurred with status=${error.status}. Consult with IST for the error=${error.message}`,
            ),
          );
          this.location.back();
        }
        // Return and throw `error` for error notification
        // rather than hide `error`
        return throwError(error);
      }),
    );
  }
}
